import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './AuthContext';
import App from './App';
import LoginPage from './LoginPage';
import RegisterPage from './RegisterPage';
import AdminDashboard from './AdminDashboard';
import ProfilePage from './ProfilePage';
import TeamPage from './TeamPage';
import LandingPage from './LandingPage';
import Paywall from './Paywall';
import ForgotPasswordPage from './ForgotPasswordPage';
import ResetPasswordPage from './ResetPasswordPage';
import { ModalProvider } from './components/CustomModal';
import { Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';

import API from './apiConfig';

// Email Verification
function VerifyEmailPage({ onSwitch }) {
  const [status, setStatus] = useState('verifying');
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) { setStatus('error'); return; }
    axios.get(`${API}/api/auth/verify-email?token=${token}`).then(() => setStatus('success')).catch(() => setStatus('error'));
  }, []);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm text-center">
        <img src="/logo.png" alt="LAIzyBlueprint" className="h-24 mx-auto mb-4" />
        {status === 'verifying' && <p className="text-zinc-400 font-mono text-sm">E-Mail wird bestätigt...</p>}
        {status === 'success' && <div className="space-y-4"><p className="text-green-400 font-mono text-sm">E-Mail erfolgreich bestätigt!</p>
          <button onClick={() => { window.history.replaceState({}, '', '/'); onSwitch('login'); }} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white font-mono text-sm rounded-lg">Zum Login</button></div>}
        {status === 'error' && <div className="space-y-4"><p className="text-red-400 font-mono text-sm">Ungültiger oder abgelaufener Link.</p>
          <button onClick={() => onSwitch('login')} className="text-blue-400 hover:text-blue-300 text-sm font-mono">Zum Login</button></div>}
      </div>
    </div>
  );
}

// Payment Success
function PaymentSuccessPage({ onDone }) {
  const [status, setStatus] = useState('checking');
  const { refreshUser } = useAuth();
  const sessionId = new URLSearchParams(window.location.search).get('session_id');

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/stripe/checkout-status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
          return;
        }
        if (data.status === 'expired') { setStatus('error'); return; }
      } catch {}
      attempts++;
      if (attempts < 10) setTimeout(poll, 2000);
      else setStatus('timeout');
    };
    poll();
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm text-center space-y-4">
        <img src="/logo.png" alt="LAIzyBlueprint" className="h-24 mx-auto" />
        {status === 'checking' && <><Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto" /><p className="text-zinc-400 font-mono text-sm">Zahlung wird überprüft...</p></>}
        {status === 'success' && <><CheckCircle className="w-12 h-12 text-green-400 mx-auto" /><p className="text-green-400 font-mono text-lg font-bold">Willkommen bei LAIzyBlueprint Pro!</p>
          <p className="text-zinc-400 font-mono text-sm">Ihr Abonnement ist jetzt aktiv.</p>
          <button onClick={() => { window.history.replaceState({}, '', '/'); onDone(); }} className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white font-mono text-sm rounded-lg">Weiter zur App</button></>}
        {(status === 'error' || status === 'timeout') && <><p className="text-amber-400 font-mono text-sm">Zahlungsstatus unklar. Bitte prüfen Sie Ihr Profil.</p>
          <button onClick={() => { window.history.replaceState({}, '', '/'); onDone(); }} className="px-6 py-2 bg-blue-600 text-white font-mono text-sm rounded-lg">Weiter</button></>}
      </div>
    </div>
  );
}

function AuthGate() {
  const { user, loading } = useAuth();
  const [page, setPage] = useState('landing');

  useEffect(() => {
    const path = window.location.pathname;
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    if (path === '/verify-email') setPage('verify');
    else if (path === '/reset-password') setPage('reset');
    else if (path === '/payment-success') setPage('payment-success');
    else if (path === '/admin') setPage('admin');
    else if (path === '/profile') setPage('profile');
    else if (params.get('ref') || hash === '#register') setPage('register');
  }, []);

  if (loading) return <div className="min-h-screen bg-zinc-950 flex items-center justify-center"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>;

  // Public pages
  if (page === 'verify') return <VerifyEmailPage onSwitch={setPage} />;
  if (page === 'reset') return <ResetPasswordPage onLogin={() => setPage('login')} />;
  if (page === 'forgot-password') return <ForgotPasswordPage onBack={() => setPage('login')} />;

  // Authenticated pages
  if (user) {
    if (page === 'payment-success') return <PaymentSuccessPage onDone={() => setPage('app')} />;
    if (page === 'admin' && user.role === 'admin') return <AdminDashboard onBack={() => { window.history.replaceState({}, '', '/'); setPage('app'); }} />;
    if (page === 'profile') return <ProfilePage onBack={() => { window.history.replaceState({}, '', '/'); setPage('app'); }} />;
    if (page === 'team') return <TeamPage onBack={() => { window.history.replaceState({}, '', '/'); setPage('app'); }} />;
    // Authenticated users go straight to the app (skip landing)
    if (page === 'landing') return <App onNavigate={setPage} />;

    // Paywall on first visit unless admin / active subscription / already dismissed in session
    const isPrivileged = user.role === 'admin' || user.subscription_status === 'active';
    const dismissed = sessionStorage.getItem('paywall_dismissed') === '1';
    if (!isPrivileged && !dismissed && page !== 'paywall' && page !== 'app' && page !== 'landing' && page !== 'team') {
      return (
        <Paywall
          onTrialAccept={() => {
            sessionStorage.setItem('paywall_dismissed', '1');
            setPage('app');
          }}
        />
      );
    }
    if (page === 'paywall') {
      return (
        <Paywall
          onTrialAccept={() => { sessionStorage.setItem('paywall_dismissed', '1'); setPage('app'); }}
          onClose={() => setPage('app')}
        />
      );
    }
    return <App onNavigate={setPage} />;
  }

  // Not authenticated
  if (page === 'register') return <RegisterPage onSwitch={setPage} />;
  if (page === 'forgot-password') return <ForgotPasswordPage onBack={() => setPage('login')} />;
  if (page === 'login') return <LoginPage onSwitch={setPage} />;
  return <LandingPage onLogin={() => setPage('login')} onRegister={() => setPage('register')} />;
}

export default function AppShell() {
  return (
    <AuthProvider>
      <ModalProvider>
        <AuthGate />
      </ModalProvider>
    </AuthProvider>
  );
}
